import React, { useMemo } from 'react';
import { useCall, CallStatus } from '@/contexts/CallContext';
import { useChat } from '@/contexts/ChatContext';
import { VideoTile } from './VideoTile';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  Users,
  Phone,
} from 'lucide-react';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const VISIBLE_STATUSES: CallStatus[] = ['initiating', 'ringing', 'connecting', 'in-call'];

export function ActiveCallWindow() {
  const { callState, endCall, toggleAudio, toggleVideo } = useCall();
  const { currentUser, teamMembers } = useChat();

  const { status, callType, isGroupCall, localStream, remoteStreams, isAudioEnabled, isVideoEnabled, callDuration, participants } = callState;
  const isVideo = callType === 'video';
  const isConnected = status === 'in-call';

  const remoteEntries = useMemo(() => {
    const entries: Array<{ userId: string; name: string; stream: MediaStream; audio: boolean; video: boolean }> = [];
    for (const [userId, stream] of remoteStreams) {
      const member = teamMembers?.find(m => m.id === userId);
      const participant = participants.find(p => p.userId === userId);
      entries.push({
        userId,
        name: member?.full_name || participant?.userName || 'Participant',
        stream,
        audio: participant?.audio ?? true,
        video: participant?.video ?? isVideo,
      });
    }
    return entries;
  }, [remoteStreams, teamMembers, participants, isVideo]);

  const isVisible = VISIBLE_STATUSES.includes(callState.status);
  if (!isVisible) return null;

  const totalParticipants = remoteEntries.length + 1; // +1 for self

  const getGridClass = () => {
    if (totalParticipants <= 1) return 'grid-cols-1';
    if (totalParticipants === 2) return 'grid-cols-1 md:grid-cols-2';
    if (totalParticipants <= 4) return 'grid-cols-2';
    return 'grid-cols-2 md:grid-cols-3';
  };

  return (
    <div className="fixed inset-0 z-[99] bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between px-6 py-3 bg-gray-800/80">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-white border-white/20 text-xs">
            {isVideo ? <Video className="h-3 w-3 mr-1" /> : <Phone className="h-3 w-3 mr-1" />}
            {isVideo ? 'Video Call' : 'Audio Call'}
          </Badge>
          {isGroupCall && (
            <Badge variant="outline" className="text-white border-white/20 text-xs">
              <Users className="h-3 w-3 mr-1" />
              {totalParticipants}
            </Badge>
          )}
        </div>

        <div className="text-white text-sm font-mono">
          {isConnected ? (
            formatDuration(callDuration)
          ) : (
            <span className="text-yellow-400 animate-pulse">
              {status === 'ringing' ? 'Ringing...' : status === 'connecting' ? 'Connecting...' : 'Starting...'}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 p-3 overflow-hidden">
        {isConnected || status === 'connecting' ? (
          <div className={`grid gap-3 h-full ${getGridClass()}`}>
            {remoteEntries.map(entry => (
              <VideoTile
                key={entry.userId}
                stream={entry.stream}
                name={entry.name}
                isMuted={!entry.audio}
                isVideoOff={!entry.video}
                className="min-h-[200px]"
              />
            ))}

            {remoteEntries.length === 0 ? (
              <VideoTile
                stream={localStream}
                name={currentUser?.full_name || 'You'}
                avatarUrl={currentUser?.avatar_url}
                isLocal
                isMuted={!isAudioEnabled}
                isVideoOff={!isVideoEnabled}
                className="min-h-[200px]"
              />
            ) : (
              <div className="fixed bottom-24 right-6 z-10 w-40 h-28 md:w-52 md:h-36 shadow-2xl rounded-xl overflow-hidden border-2 border-white/20">
                <VideoTile
                  stream={localStream}
                  name="You"
                  avatarUrl={currentUser?.avatar_url}
                  isLocal
                  isMuted={!isAudioEnabled}
                  isVideoOff={!isVideoEnabled}
                  className="w-full h-full"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-blue-400/20 animate-ping" />
              <div className="h-28 w-28 rounded-full bg-gray-700 flex items-center justify-center relative z-10">
                {isVideo ? (
                  <Video className="h-12 w-12 text-white" />
                ) : (
                  <Phone className="h-12 w-12 text-white" />
                )}
              </div>
            </div>
            <p className="text-white text-lg font-medium animate-pulse">
              {status === 'ringing' ? 'Ringing...' : 'Calling...'}
            </p>

            {localStream && isVideo && (
              <div className="fixed bottom-24 right-6 z-10 w-40 h-28 md:w-52 md:h-36 shadow-2xl rounded-xl overflow-hidden border-2 border-white/20">
                <VideoTile
                  stream={localStream}
                  name="You"
                  avatarUrl={currentUser?.avatar_url}
                  isLocal
                  isMuted={!isAudioEnabled}
                  isVideoOff={!isVideoEnabled}
                  className="w-full h-full"
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-4 py-5 bg-gray-800/80">
        <Button
          variant="ghost"
          size="icon"
          className={`h-14 w-14 rounded-full ${isAudioEnabled ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}
          onClick={toggleAudio}
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
        >
          {isAudioEnabled ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
        </Button>

        {isVideo && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-14 w-14 rounded-full ${isVideoEnabled ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}
            onClick={toggleVideo}
            title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {isVideoEnabled ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-14 w-14 rounded-full bg-red-500 hover:bg-red-600 text-white"
          onClick={endCall}
          title="End call"
        >
          <PhoneOff className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
}
